import React, {
  createContext,
  useState,
  useMemo,
  ReactNode,
  useEffect,
} from "react";
import { ThemeProvider, Theme } from "@mui/material/styles";
import {
  darkTheme,
  Orange,
  Purple,
  Brown,
  OrangeGradient,
  PurpleGradient,
  BrownGradient,
  DarkGradient,
  createCustomTheme,
  customGradient,
} from "./themes";
import { useGetThemesQuery } from "@/features/Roles/api";

type ThemeName = "light" | "dark" | "Orange" | "Purple" | "Brown" | any;

interface ThemeContextType {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  themeGradient: string;
  customThemes: any;
  isThemeDataLoading: boolean;
}

const themes: Record<ThemeName, Theme> = {
  light: Purple,
  dark: darkTheme,
  Orange: Orange,
  Purple: Purple,
  Brown: Brown,
};

export const ThemeContext = createContext<ThemeContextType>({
  theme: (localStorage.getItem("theme") as ThemeName) || "dark",
  setTheme: () => {},
  themeGradient: "",
  customThemes: [],
  isThemeDataLoading: false,
});

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({
  children,
}) => {
  const [customThemes, setCustomThemes] = useState([]);
  const [theme, setTheme] = useState<ThemeName>(
    (localStorage.getItem("theme") as ThemeName) || "dark"
  );

  const { data, isSuccess, isLoading, isFetching } = useGetThemesQuery({});

  useEffect(() => {
    if (isSuccess && data) {
      data?.data?.decryptedData?.items
        ?.filter((v) => v.isCustom)
        .forEach((themeData) => {
          themes[themeData.name] = createCustomTheme(themeData);
        });
      setCustomThemes(data?.data?.decryptedData?.items || []);
    }
  }, [isSuccess, data]);

  const themeGradient = useMemo(() => {
    if (isLoading || isFetching) {
      return PurpleGradient("right");
    }
    const currentThemeData = customThemes?.find((t) => t.name === theme);

    if (currentThemeData?.isCustom) {
      return customGradient(
        "right",
        currentThemeData.primaryColor,
        currentThemeData.secondaryColor
      );
    } else {
      switch (theme) {
        case "Orange":
          return OrangeGradient("right");
        case "Purple":
          return PurpleGradient("right");
        case "Brown":
          return BrownGradient("right");
        case "dark":
          return DarkGradient("right");
        default:
          return "transparent";
      }
    }
  }, [theme, customThemes, isLoading, isFetching]);

  const muiTheme = useMemo(() => {
    if (isLoading || isFetching) {
      return Purple;
    }
    return themes[theme] || Purple;
  }, [theme, customThemes, isLoading, isFetching]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        themeGradient,
        customThemes,
        isThemeDataLoading: isLoading || isFetching,
      }}
    >
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
